import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  reload,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../firebase';
import { getAllModuleProgress, syncUserExportFieldsToFirestore } from '../services/lmsData';
import { validatePassword } from '../utils/passwordPolicy';
import { resolveParticipantDisplayName } from '../utils/participantDisplayName';

const AuthContext = createContext(null);

/** Maps Firebase User to app shape; `displayNameRaw` is unset empty string for edit forms (certificate uses raw then email). */
function mapFirebaseAuthUser(firebaseUser) {
  if (!firebaseUser) return null;
  const raw = firebaseUser.displayName != null ? String(firebaseUser.displayName).trim() : '';
  const emailFromProvider = firebaseUser.providerData?.find((p) => p?.email)?.email || null;
  const email = firebaseUser.email || emailFromProvider || null;
  const resolved = resolveParticipantDisplayName({
    displayNameRaw: raw,
    displayName: raw,
    email,
    fallbackLabel: 'User',
  });
  return {
    uid: firebaseUser.uid,
    email,
    displayName: resolved,
    displayNameRaw: raw,
    photoURL: firebaseUser.photoURL,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [demoUser, setDemoUser] = useState(null);
  const [moduleProgress, setModuleProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const useFirebase = isFirebaseConfigured();

  const refreshModuleProgress = useCallback(async () => {
    if (!useFirebase || !user?.uid) {
      setModuleProgress([]);
      return;
    }
    const list = await getAllModuleProgress(user.uid);
    setModuleProgress(list);
  }, [useFirebase, user?.uid]);

  useEffect(() => {
    if (!useFirebase) {
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const mapped = mapFirebaseAuthUser(firebaseUser);
      setUser(mapped);
      if (firebaseUser && mapped?.uid) {
        void syncUserExportFieldsToFirestore(
          mapped.uid,
          mapped.email,
          mapped.displayNameRaw || mapped.displayName || ''
        );
      }
      if (!firebaseUser) {
        setModuleProgress([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [useFirebase]);

  useEffect(() => {
    let active = true;
    const loadProgress = async () => {
      if (!useFirebase || !user?.uid) {
        if (active) setModuleProgress([]);
        return;
      }
      const list = await getAllModuleProgress(user.uid);
      if (active) setModuleProgress(list);
    };
    void loadProgress();
    return () => {
      active = false;
    };
  }, [useFirebase, user?.uid]);

  const login = async (email, password) => {
    if (!useFirebase) {
      setDemoUser({
        name: email?.split('@')[0] || 'Guest',
        email: email || 'guest@example.com',
        role: 'Student',
        avatar: null,
        enrolledModules: 3,
        completedModules: 1,
        progress: 65,
      });
      return { success: true, demo: true };
    }
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const signup = async (email, password, displayName = '') => {
    const trimmedName = String(displayName || '').trim();
    if (!trimmedName) {
      return { success: false, error: 'Please enter your name.' };
    }
    const pwCheck = validatePassword(password);
    if (!pwCheck.ok) {
      return { success: false, error: pwCheck.message };
    }
    if (!useFirebase) {
      setDemoUser({
        name: trimmedName,
        email: email || '',
        role: 'Student',
        avatar: null,
        enrolledModules: 3,
        completedModules: 1,
        progress: 65,
      });
      return { success: true, demo: true };
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const u = userCredential.user;
      if (u) {
        await updateProfile(u, { displayName: trimmedName });
        await reload(u);
      }
      const mapped = mapFirebaseAuthUser(auth.currentUser || u);
      if (mapped) setUser(mapped);
      if (u?.uid) {
        void syncUserExportFieldsToFirestore(
          u.uid,
          mapped?.email || u.email,
          mapped?.displayNameRaw || trimmedName
        );
      }
      return { success: true, user: userCredential.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const logout = async () => {
    if (useFirebase) {
      await signOut(auth);
    } else {
      setDemoUser(null);
    }
  };

  /**
   * Sends Firebase password-reset email. Treats unknown emails as success to reduce account enumeration.
   * @param {string} email
   * @returns {Promise<{ success: boolean, error?: string }>}
   */
  /**
   * @param {string} displayName
   * @returns {Promise<{ success: boolean, error?: string }>}
   */
  const updateUserDisplayName = async (displayName) => {
    const trimmed = String(displayName || '').trim();
    if (!useFirebase) {
      setDemoUser((prev) =>
        prev
          ? {
              ...prev,
              name: trimmed || prev.email?.split('@')[0] || 'User',
            }
          : null
      );
      return { success: true };
    }
    const u = auth?.currentUser;
    if (!u) return { success: false, error: 'Not signed in.' };
    try {
      await updateProfile(u, { displayName: trimmed || null });
      await reload(u);
      const next = mapFirebaseAuthUser(auth.currentUser);
      setUser(next);
      if (next?.uid) {
        void syncUserExportFieldsToFirestore(next.uid, next.email, next.displayNameRaw || next.displayName || '');
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.message || 'Could not update display name.' };
    }
  };

  /**
   * @param {string} currentPassword
   * @param {string} newPassword
   * @returns {Promise<{ success: boolean, error?: string }>}
   */
  const changeUserPassword = async (currentPassword, newPassword) => {
    if (!useFirebase) {
      return { success: false, error: 'Password change is not available in demo mode.' };
    }
    const u = auth?.currentUser;
    if (!u?.email) return { success: false, error: 'No email on this account.' };
    const hasPassword = u.providerData?.some((p) => p.providerId === 'password');
    if (!hasPassword) {
      return {
        success: false,
        error: 'This account does not use email/password. Use your provider’s account settings or reset link from sign-in.',
      };
    }
    const cur = String(currentPassword || '');
    const next = String(newPassword || '');
    const pwCheck = validatePassword(next);
    if (!pwCheck.ok) {
      return { success: false, error: pwCheck.message };
    }
    try {
      const cred = EmailAuthProvider.credential(u.email, cur);
      await reauthenticateWithCredential(u, cred);
      await updatePassword(u, next);
      return { success: true };
    } catch (err) {
      const code = err?.code;
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        return { success: false, error: 'Current password is incorrect.' };
      }
      if (code === 'auth/weak-password') {
        return { success: false, error: 'New password is too weak.' };
      }
      if (code === 'auth/requires-recent-login') {
        return { success: false, error: 'Please sign out and sign in again, then try changing your password.' };
      }
      return { success: false, error: err?.message || 'Could not update password.' };
    }
  };

  const requestPasswordReset = async (email) => {
    const trimmed = (email || '').trim();
    if (!trimmed) {
      return { success: false, error: 'Please enter your email address.' };
    }
    if (!useFirebase) {
      return {
        success: false,
        error: 'Password reset by email is not available in demo mode. Connect Firebase to enable it.',
      };
    }
    try {
      await sendPasswordResetEmail(auth, trimmed);
      return { success: true };
    } catch (err) {
      const code = err?.code;
      if (code === 'auth/user-not-found') {
        return { success: true };
      }
      if (code === 'auth/invalid-email') {
        return { success: false, error: 'Please enter a valid email address.' };
      }
      if (code === 'auth/too-many-requests') {
        return { success: false, error: 'Too many attempts. Please try again later.' };
      }
      if (code === 'auth/operation-not-allowed') {
        return {
          success: false,
          error: 'Password reset is not enabled for this sign-in method. Contact your administrator.',
        };
      }
      return { success: false, error: err?.message || 'Could not send reset email. Please try again.' };
    }
  };

  const getAppUser = () => {
    if (user)
      return {
        name: resolveParticipantDisplayName({
          displayNameRaw: user.displayNameRaw,
          displayName: user.displayName,
          email: user.email,
          fallbackLabel: 'User',
        }),
        email: user.email,
        role: 'Student',
        avatar: user.photoURL || null,
        enrolledModules: moduleProgress.filter((m) => m.isEnrolled || m.status === 'in-progress' || m.status === 'completed').length,
        completedModules: moduleProgress.filter((m) => m.status === 'completed' || m.isCompleted).length,
        progress: moduleProgress.length
          ? Math.round(
              moduleProgress.reduce((sum, m) => sum + (Number(m.progressPercent) || 0), 0) / moduleProgress.length
            )
          : 0,
      };
    if (demoUser) return demoUser;
    return null;
  };

  const appUser = getAppUser();
  const value = {
    user: appUser,
    firebaseUser: user,
    moduleProgress,
    refreshModuleProgress,
    loading,
    useFirebase,
    login,
    signup,
    logout,
    requestPasswordReset,
    updateUserDisplayName,
    changeUserPassword,
    isAuthenticated: !!appUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
